import { Suspense, useContext, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { Toaster as Sonner, toast } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider, useApp, type Role } from "@/context/AppContext";
import { AuthProvider, AuthContext } from "@/store/AuthContext";
import { isDevMode } from "@/lib/firebase";
import { LoadingScreen } from "@/pages/LoadingScreen";
import { ErrorScreen } from "@/pages/ErrorScreen";
import { AppRoutes } from "./AppRoutes";
import { initSentry, Sentry } from "@/lib/sentry";
import { useDidYouKnow } from "@/hooks/useDidYouKnow";

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 300_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Syncs the real role from Firestore (via AuthContext.userProfile) into AppContext
// so the app always dispatches to the correct role-specific screen after login.
function RoleSyncer() {
  const auth = useContext(AuthContext);
  const { setRole } = useApp();
  useEffect(() => {
    // In dev mode (and for unauthenticated users) localStorage is the source of truth.
    // Only sync from Firestore when the user is authenticated and their profile is loaded.
    if (isDevMode()) return;
    if (auth?.isAuthenticated && auth?.userProfile?.role) {
      setRole(auth.userProfile.role as Role);
    }
  }, [auth?.userProfile?.role, auth?.isAuthenticated, setRole]);
  return null;
}

// Warms the React Query cache with Did You Know facts before any page loads,
// so LoadingScreen can read them synchronously from the cache.
function FactsPrefetcher() {
  useDidYouKnow();
  return null;
}

function PwaUpdateListener() {
  useEffect(() => {
    const handler = (e: Event) => {
      const { update } = (e as CustomEvent).detail;
      toast("Une mise à jour est disponible", {
        action: { label: "Mettre à jour", onClick: update },
        duration: Infinity,
      });
    };
    window.addEventListener("pwa-update-available", handler);
    return () => window.removeEventListener("pwa-update-available", handler);
  }, []);
  return null;
}

const App = () => (
  <Sentry.ErrorBoundary fallback={({ error }) => <ErrorScreen error={error as Error} />}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner position="top-center" />
        <PwaUpdateListener />
        <AppProvider>
          <AuthProvider>
            <RoleSyncer />
            <FactsPrefetcher />
            <BrowserRouter>
              <Suspense fallback={<LoadingScreen />}>
                <AppRoutes />
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </AppProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
